import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../supabaseClient'
import { useAuth } from '../contexts/AuthContext'

function Messages() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const withUserId = searchParams.get('with')

  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)

  // Compose state
  const [showCompose, setShowCompose] = useState(false)
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState([])
  const [searchLoading, setSearchLoading] = useState(false)
  const [composing, setComposing] = useState(false)
  const searchRef = useRef(null)

  useEffect(() => {
    if (!user) return
    if (withUserId) {
      findOrCreateConversation(withUserId)
    } else {
      fetchConversations()
    }
  }, [user, withUserId])

  // Auto-focus search when compose opens
  useEffect(() => {
    if (showCompose) searchRef.current?.focus()
  }, [showCompose])

  // Search users as you type
  useEffect(() => {
    if (!userSearch.trim()) { setUserResults([]); return }
    const timer = setTimeout(() => searchUsers(userSearch.trim()), 300)
    return () => clearTimeout(timer)
  }, [userSearch])

  const searchUsers = async (query) => {
    setSearchLoading(true)
    const { data } = await supabase
      .from('users')
      .select('id, username')
      .ilike('username', `%${query}%`)
      .neq('id', user.id)
      .limit(8)
    setUserResults(data || [])
    setSearchLoading(false)
  }

  const findOrCreateConversation = async (otherUserId) => {
    const [p1, p2] = [user.id, otherUserId].sort()
    setComposing(true)
    try {
      const { data: existing } = await supabase
        .from('conversations')
        .select('conversation_id')
        .eq('participant_1', p1)
        .eq('participant_2', p2)
        .is('trade_id', null)
        .single()

      if (existing) {
        navigate(`/messages/${existing.conversation_id}`, { replace: true })
        return
      }

      const { data: created, error } = await supabase
        .from('conversations')
        .insert({ participant_1: p1, participant_2: p2 })
        .select('conversation_id')
        .single()

      if (error) throw error
      navigate(`/messages/${created.conversation_id}`, { replace: true })
    } catch (err) {
      console.error('Error finding/creating conversation:', err)
      fetchConversations()
    } finally {
      setComposing(false)
    }
  }

  const fetchConversations = async () => {
    try {
      const { data: convData, error } = await supabase
        .from('conversations')
        .select('conversation_id, participant_1, participant_2, updated_at, trade_id')
        .order('updated_at', { ascending: false })

      if (error) throw error

      if (!convData || convData.length === 0) {
        setConversations([])
        setLoading(false)
        return
      }

      const otherIds = convData.map(c =>
        c.participant_1 === user.id ? c.participant_2 : c.participant_1
      )
      const { data: usersData } = await supabase
        .from('users')
        .select('id, username')
        .in('id', [...new Set(otherIds)])

      const userMap = {}
      usersData?.forEach(u => { userMap[u.id] = u.username })

      const convIds = convData.map(c => c.conversation_id)
      const { data: allMessages } = await supabase
        .from('messages')
        .select('conversation_id, body, sender_id, created_at, is_read, is_trade_offer, is_system_message')
        .in('conversation_id', convIds)
        .order('created_at', { ascending: false })

      const lastMessageMap = {}
      const unreadMap = {}
      allMessages?.forEach(msg => {
        if (!lastMessageMap[msg.conversation_id]) lastMessageMap[msg.conversation_id] = msg
        if (!msg.is_read && msg.sender_id !== user.id) {
          unreadMap[msg.conversation_id] = (unreadMap[msg.conversation_id] || 0) + 1
        }
      })

      setConversations(convData.map(c => ({
        ...c,
        otherUsername: userMap[c.participant_1 === user.id ? c.participant_2 : c.participant_1] || 'Unknown',
        otherId: c.participant_1 === user.id ? c.participant_2 : c.participant_1,
        lastMessage: lastMessageMap[c.conversation_id] || null,
        unreadCount: unreadMap[c.conversation_id] || 0,
        isTradeConversation: !!c.trade_id,
      })))
    } catch (err) {
      console.error('Error fetching conversations:', err)
    } finally {
      setLoading(false)
    }
  }

  if (!user) {
    return (
      <div className="error-page">
        <h2>Login Required</h2>
        <Link to="/login" className="btn-primary">Log In</Link>
      </div>
    )
  }

  if (loading) return <div className="loading">Loading messages...</div>

  return (
    <div className="messages-page">
      <div className="messages-header">
        <h1>Messages</h1>
        <button
          className="btn-primary"
          onClick={() => { setShowCompose(c => !c); setUserSearch(''); setUserResults([]) }}
        >
          {showCompose ? '✕ Cancel' : '+ New Message'}
        </button>
      </div>

      {/* Compose panel */}
      {showCompose && (
        <div className="compose-panel">
          <p className="compose-label">Search for a member to message:</p>
          <div className="compose-search-wrap">
            <input
              ref={searchRef}
              type="text"
              className="filter-input"
              placeholder="Type a username..."
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
            />
            {searchLoading && <span className="compose-searching">Searching…</span>}
          </div>

          {userResults.length > 0 && (
            <div className="compose-results">
              {userResults.map(u => (
                <button
                  key={u.id}
                  className="compose-result-item"
                  onClick={() => findOrCreateConversation(u.id)}
                  disabled={composing}
                >
                  <div className="conversation-avatar" style={{ width: 32, height: 32, fontSize: '0.85rem' }}>
                    {u.username.charAt(0).toUpperCase()}
                  </div>
                  <span>{u.username}</span>
                  {composing && <span style={{ marginLeft: 'auto', color: '#94a3b8' }}>Opening…</span>}
                </button>
              ))}
            </div>
          )}

          {userSearch.trim() && !searchLoading && userResults.length === 0 && (
            <p className="compose-no-results">No members found matching "{userSearch}"</p>
          )}
        </div>
      )}

      {/* Conversation list */}
      {conversations.length === 0 && !showCompose ? (
        <div className="no-content-box">
          <p>No conversations yet.</p>
          <button
            className="btn-primary"
            style={{ marginTop: '1rem' }}
            onClick={() => setShowCompose(true)}
          >
            Start Your First Conversation
          </button>
        </div>
      ) : conversations.length > 0 && (
        <div className="conversation-list">
          {conversations.map(conv => (
            <Link
              key={conv.conversation_id}
              to={`/messages/${conv.conversation_id}`}
              className={`conversation-item${conv.unreadCount > 0 ? ' conversation-item-unread' : ''}`}
            >
              <div className="conversation-avatar">
                {conv.otherUsername?.charAt(0).toUpperCase()}
              </div>
              <div className="conversation-info">
                <div className="conversation-name-row">
                  <span className="conversation-username">
                    {conv.otherUsername}
                    {conv.isTradeConversation && (
                      <span className="conv-trade-badge">📦 Trade</span>
                    )}
                  </span>
                  <span className="conversation-time">
                    {conv.lastMessage
                      ? new Date(conv.lastMessage.created_at).toLocaleDateString()
                      : new Date(conv.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="conversation-preview-row">
                  <span className="conversation-preview">
                    {conv.lastMessage
                      ? conv.lastMessage.is_trade_offer
                        ? `${conv.lastMessage.sender_id === user.id ? 'You: ' : ''}📦 Trade Offer`
                        : conv.lastMessage.is_system_message
                        ? '🔔 ' + conv.lastMessage.body.split('\n')[0]
                        : `${conv.lastMessage.sender_id === user.id ? 'You: ' : ''}${conv.lastMessage.body}`
                      : 'No messages yet'}
                  </span>
                  {conv.unreadCount > 0 && (
                    <span className="conversation-unread-badge">{conv.unreadCount}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

export default Messages
